/**
 * MBS Kurumsal Gösterge Paneli — dosya bazlı detay (ad soyad, TC, dosya no)
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    Briefcase,
    Calendar,
    ChevronRight,
    FolderOpen,
    Handshake,
    RefreshCw,
    Scale,
    Shield,
    Users,
    Wallet,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    QUEUE_DEFS,
    QUEUE_TONE,
    resolveQueue,
    useDashboardModel,
} from "./dashboardShared";
import { FileTable } from "./DashboardPro";
import { formatCurrency, formatTR, tooltipStyle } from "./theme";
import SystemHealthBar from "./SystemHealthBar";

const QUEUE_LINK = "/e/sigorta-dosyalari";

const MAIN_MODULES = [
    {
        id: "dosya",
        label: "Dosya Operasyon",
        short: "Dosya",
        icon: Briefcase,
        dot: "bg-[#D4AF37]",
        accent: "gold",
        desc: "Müvekkil ad soyad, TC/VKN ve dosya numarası — kategoriye tıklayın",
    },
    {
        id: "odeme",
        label: "Ödeme Takipleri",
        short: "Ödeme",
        icon: Wallet,
        dot: "bg-emerald-400",
        accent: "emerald",
        desc: "Bekleyen ve dönem ödemeleri — müvekkil, TC, dosya no ve tutar",
    },
    {
        id: "araci",
        label: "Aracı Performans",
        short: "Aracı",
        icon: Handshake,
        dot: "bg-violet-400",
        accent: "violet",
        desc: "Aracı bazlı dosya/müvekkil sayısı ve ödeme detayları",
    },
];

function ModuleTabButton({ mod, active, count, onClick }) {
    const Icon = mod.icon;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`shrink-0 inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                active
                    ? "bg-white text-slate-900 shadow-lg shadow-black/20"
                    : "border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 hover:border-white/20"
            }`}
        >
            <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${active ? "text-slate-900" : ""}`} />
            <span className="hidden xs:inline sm:inline">{mod.label}</span>
            <span className="xs:hidden sm:hidden">{mod.short}</span>
            {count != null && count > 0 && (
                <span
                    className={`tabular-nums font-bold text-[10px] px-1.5 py-0.5 rounded-md ${
                        active
                            ? "bg-rose-500 text-white"
                            : mod.id === "odeme"
                              ? "bg-emerald-500/20 text-emerald-300"
                              : mod.id === "araci"
                                ? "bg-violet-500/20 text-violet-300"
                                : "bg-amber-500/20 text-amber-300"
                    }`}
                >
                    {count}
                </span>
            )}
        </button>
    );
}

function KpiMini({ label, value, sub, accent = "gold" }) {
    const styles = {
        gold: "border-[#D4AF37]/25 bg-[#D4AF37]/5 text-[#F3E5AB]",
        emerald: "border-emerald-500/25 bg-emerald-500/5 text-emerald-200",
        violet: "border-violet-500/25 bg-violet-500/5 text-violet-200",
        rose: "border-rose-500/25 bg-rose-500/5 text-rose-200",
        sky: "border-sky-500/25 bg-sky-500/5 text-sky-200",
    };
    return (
        <div className={`rounded-xl border px-3 py-2.5 ${styles[accent] || styles.gold}`}>
            <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-base sm:text-lg font-bold tabular-nums mt-0.5">{value}</p>
            {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
        </div>
    );
}

function AraciPerfBar({ odenen, bekleyen }) {
    const total = (odenen || 0) + (bekleyen || 0);
    const pct = total > 0 ? Math.round((odenen / total) * 100) : 0;
    return (
        <div className="mt-2">
            <div className="flex justify-between text-[9px] text-slate-500 mb-1">
                <span>Ödenen %{pct}</span>
                <span>{formatCurrency(odenen)} / {formatCurrency(total)}</span>
            </div>
            <div className="h-1 rounded-full bg-black/40 overflow-hidden">
                <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function CompactKpi({ label, value, href, urgent, accent = "gold" }) {
    const colors = {
        gold: "text-[#F3E5AB] border-[#D4AF37]/30",
        rose: "text-rose-300 border-rose-500/30",
        violet: "text-violet-300 border-violet-500/30",
        sky: "text-sky-300 border-sky-500/30",
        emerald: "text-emerald-300 border-emerald-500/30",
    };
    const cls = `rounded-xl border bg-[#0a1018]/80 px-3 py-2.5 ${colors[accent] || colors.gold}`;
    const inner = (
        <>
            <p className="text-[9px] uppercase tracking-wider text-slate-500 truncate">
                {label}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-lg font-bold tabular-nums">{value}</p>
                {urgent ? (
                    <span className="text-[8px] font-bold text-rose-400 bg-rose-500/15 px-1 rounded">
                        !
                    </span>
                ) : null}
            </div>
        </>
    );
    if (href) {
        return (
            <Link to={href} className={`${cls} hover:bg-white/[0.03] transition block`}>
                {inner}
            </Link>
        );
    }
    return <div className={cls}>{inner}</div>;
}

function QueuePanel({ def, model, defaultOpen = false }) {
    const { rows, count } = resolveQueue(def, model);
    const [open, setOpen] = useState(defaultOpen || count > 0);
    const st = QUEUE_TONE[def.tone] || QUEUE_TONE.sky;

    return (
        <div
            className={`rounded-2xl border border-white/[0.08] bg-[#0a1018]/70 overflow-hidden ${
                count > 0 ? st.border : ""
            }`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/[0.02] transition"
            >
                <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${st.dot}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-white">{def.title}</p>
                        <span
                            className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-lg ${st.badge}`}
                        >
                            {formatTR(count)} dosya
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                        {def.help}
                    </p>
                </div>
                <ChevronRight
                    className={`h-4 w-4 text-slate-500 shrink-0 transition-transform ${
                        open ? "rotate-90" : ""
                    }`}
                />
            </button>
            {open && (
                <div className="px-3 pb-3 border-t border-white/[0.06]">
                    {count > 0 ? (
                        <>
                            <FileTable
                                rows={rows}
                                empty="Bu kategoride dosya yok."
                            />
                            <div className="flex justify-end mt-2">
                                <Link
                                    to={QUEUE_LINK}
                                    className="text-[10px] text-[#D4AF37] hover:text-[#F3E5AB] inline-flex items-center gap-1"
                                >
                                    Tüm dosyalar <ArrowRight className="h-3 w-3" />
                                </Link>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-slate-500 text-center py-6">
                            Bu kategoride bekleyen dosya yok ✓
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function DetailTable({ columns, rows, empty, mobileRender }) {
    if (!rows?.length) {
        return (
            <p className="text-sm text-slate-500 text-center py-6">{empty}</p>
        );
    }
    return (
        <>
            <div className="md:hidden space-y-2">
                {rows.map((row, i) => mobileRender(row, i))}
            </div>
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/10">
                            {columns.map((c) => (
                                <th
                                    key={c.key}
                                    className={`pb-2 ${c.align === "right" ? "text-right pr-1" : "pl-1"}`}
                                >
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr
                                key={row.id ?? i}
                                className="border-b border-white/5 hover:bg-white/[0.03]"
                            >
                                {columns.map((c) => (
                                    <td
                                        key={c.key}
                                        className={`py-2.5 ${c.align === "right" ? "text-right pr-1" : "pl-1"} ${c.mono ? "font-mono text-xs" : ""} ${c.muted ? "text-slate-400" : "text-white"} max-w-[180px] truncate`}
                                    >
                                        {c.render ? c.render(row) : row[c.key] ?? "—"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function OdemeTakipContent({ snapshot, periodLabel, subTab, onSubTab }) {
    const ozet = snapshot?.odeme_ozet || {};
    const bekleyen = snapshot?.odeme_bekleyen || [];
    const donem = snapshot?.odeme_donem || [];

    const cols = [
        { key: "muvekkil", label: "Müvekkil Ad Soyad", render: (r) => r.muvekkilAdSoyad },
        { key: "tc", label: "TC / VKN", mono: true, muted: true, render: (r) => r.tc || "—" },
        {
            key: "dosya",
            label: "Dosya No",
            mono: true,
            render: (r) =>
                r.dosya_id ? (
                    <Link to={`/e/sigorta-dosyalari/${r.dosya_id}`} className="text-[#F3E5AB] hover:underline">
                        {r.dosyaNo}
                    </Link>
                ) : r.dosyaNo,
        },
        { key: "tur", label: "Tür", muted: true, render: (r) => r.odeme_turu || "—" },
        { key: "tarih", label: "Tarih", muted: true, render: (r) => r.tarih_label || "—" },
        {
            key: "tutar",
            label: "Tutar",
            align: "right",
            mono: true,
            render: (r) => formatCurrency(r.odeme_miktari),
        },
        {
            key: "durum",
            label: "Durum",
            align: "right",
            render: (r) => (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.odeme_yapildi ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30" : "bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30"}`}>
                    {r.odeme_yapildi ? "Ödendi" : "Bekliyor"}
                </span>
            ),
        },
    ];

    const mobileRow = (r) => (
        <Link
            key={r.id}
            to={r.dosya_id ? `/e/sigorta-dosyalari/${r.dosya_id}` : "/e/odemeler"}
            className="block rounded-xl border border-emerald-500/15 bg-gradient-to-br from-emerald-500/[0.06] to-black/20 p-3.5 hover:border-emerald-500/30 transition"
        >
            <div className="flex justify-between items-start gap-2">
                <p className="text-sm font-semibold text-white truncate">{r.muvekkilAdSoyad}</p>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${r.odeme_yapildi ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                    {r.odeme_yapildi ? "Ödendi" : "Bekliyor"}
                </span>
            </div>
            <p className="text-[11px] font-mono text-slate-400 mt-1.5">
                {r.tc && <span className="text-slate-500">TC {r.tc} · </span>}
                <span className="text-[#F3E5AB]">{r.dosyaNo}</span>
            </p>
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/5">
                <span className="text-[10px] text-slate-500 uppercase">{r.odeme_turu || "—"}</span>
                <span className="text-sm text-emerald-400 font-bold font-mono">{formatCurrency(r.odeme_miktari)}</span>
            </div>
        </Link>
    );

    const rows = subTab === "bekleyen" ? bekleyen : donem;

    return (
        <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
                <KpiMini label="Dönem kayıt" value={formatTR(ozet.donem_kayit)} accent="emerald" sub={periodLabel} />
                <KpiMini label="Ödendi" value={formatTR(ozet.donem_odendi)} accent="gold" />
                <KpiMini label="Bekleyen" value={formatTR(ozet.bekleyen_sayisi)} accent="rose" sub={formatCurrency(ozet.bekleyen_tutar)} />
                <KpiMini label="Dönem toplam" value={formatCurrency(ozet.donem_toplam)} accent="sky" />
                <KpiMini label="Müv. hakediş" value={formatCurrency(ozet.donem_muvekkil_hakedis)} accent="emerald" sub={`MBS ${formatCurrency(ozet.donem_mbs_hakedis)}`} />
            </div>

            <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-thin pb-1">
                {[
                    { id: "bekleyen", label: "Bekleyen ödemeler", count: bekleyen.length },
                    { id: "donem", label: "Dönem kayıtları", count: donem.length },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => onSubTab(t.id)}
                        className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition ${subTab === t.id ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-500/40" : "border border-white/10 text-slate-400 hover:text-white"}`}
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        {t.label}
                        <span className={`tabular-nums font-bold px-1.5 py-0.5 rounded text-[10px] ${subTab === t.id ? "bg-emerald-500 text-white" : "bg-white/5"}`}>{t.count}</span>
                    </button>
                ))}
            </div>

            <DetailTable
                columns={cols}
                rows={rows}
                empty={subTab === "bekleyen" ? "Bekleyen ödeme kaydı yok ✓" : "Bu dönemde ödeme kaydı yok"}
                mobileRender={mobileRow}
            />
        </>
    );
}

function AraciPerformansContent({ snapshot, subTab, onSubTab }) {
    const ozet = snapshot?.araci_ozet || {};
    const performans = snapshot?.araci_performans || [];
    const odemeler = snapshot?.araci_odeme_satirlari || [];
    const bekleyenCount = odemeler.filter((r) => r.durum !== "ODENDI").length;

    const perfCols = [
        {
            key: "araci",
            label: "Aracı",
            render: (r) => (
                <Link to={`/e/aracilar/${r.id}`} className="text-[#F3E5AB] hover:underline font-semibold">{r.araci_ad}</Link>
            ),
        },
        {
            key: "aktif",
            label: "Durum",
            render: (r) => (
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.aktif ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/25" : "bg-slate-500/20 text-slate-400"}`}>
                    {r.aktif ? "Aktif" : "Pasif"}
                </span>
            ),
        },
        { key: "dosya", label: "Getirdiği Dosya", mono: true, render: (r) => formatTR(r.dosya_sayisi) },
        { key: "muvekkil", label: "Müvekkil", mono: true, render: (r) => formatTR(r.muvekkil_sayisi) },
        { key: "odenen", label: "Ödenen", align: "right", mono: true, render: (r) => formatCurrency(r.odenen_tutar) },
        { key: "bekleyen", label: "Bekleyen", align: "right", mono: true, render: (r) => formatCurrency(r.bekleyen_tutar) },
    ];

    const odemeCols = [
        { key: "araci", label: "Aracı", render: (r) => <span className="text-violet-300 font-medium">{r.araci_ad}</span> },
        { key: "muvekkil", label: "Müvekkil Ad Soyad", render: (r) => r.muvekkilAdSoyad },
        { key: "tc", label: "TC / VKN", mono: true, muted: true, render: (r) => r.tc || "—" },
        {
            key: "dosya",
            label: "Dosya No",
            mono: true,
            render: (r) => r.dosya_id ? (
                <Link to={`/e/sigorta-dosyalari/${r.dosya_id}`} className="text-[#F3E5AB] hover:underline">{r.dosyaNo}</Link>
            ) : r.dosyaNo,
        },
        { key: "tutar", label: "Tutar", align: "right", mono: true, render: (r) => formatCurrency(r.tutar) },
        {
            key: "durum",
            label: "Durum",
            align: "right",
            render: (r) => (
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.durum === "ODENDI" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                    {r.durum_label || r.durum}
                </span>
            ),
        },
        { key: "tarih", label: "Tarih", muted: true, render: (r) => r.tarih_label || "—" },
    ];

    return (
        <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
                <KpiMini label="Toplam aracı" value={formatTR(ozet.toplam_araci)} accent="violet" sub={`${formatTR(ozet.aktif_araci)} aktif`} />
                <KpiMini label="Toplam ödenen" value={formatCurrency(ozet.araci_odeme_toplam)} accent="emerald" />
                <KpiMini label="Bekleyen ödeme" value={formatTR(bekleyenCount)} accent="rose" />
                <KpiMini label="En çok dosya" value={performans[0] ? formatTR(performans[0].dosya_sayisi) : "—"} accent="gold" sub={performans[0]?.araci_ad} />
            </div>

            <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-thin pb-1">
                {[
                    { id: "performans", label: "Aracı performans", count: performans.length },
                    { id: "odemeler", label: "Ödeme detayı", count: odemeler.length },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => onSubTab(t.id)}
                        className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition ${subTab === t.id ? "bg-violet-500/20 text-violet-200 ring-1 ring-violet-500/40" : "border border-white/10 text-slate-400 hover:text-white"}`}
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                        {t.label}
                        <span className={`tabular-nums font-bold px-1.5 py-0.5 rounded text-[10px] ${subTab === t.id ? "bg-violet-500 text-white" : "bg-white/5"}`}>{t.count}</span>
                    </button>
                ))}
            </div>

            {subTab === "performans" ? (
                <>
                    <div className="md:hidden space-y-3">
                        {performans.length ? performans.map((r) => (
                            <Link key={r.id} to={`/e/aracilar/${r.id}`} className="block rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.08] to-black/20 p-4 hover:border-violet-500/35 transition">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-sm font-bold text-white">{r.araci_ad}</p>
                                        <p className="text-xs text-slate-400 mt-1">{formatTR(r.dosya_sayisi)} dosya · {formatTR(r.muvekkil_sayisi)} müvekkil</p>
                                    </div>
                                    <span className={`text-[9px] px-2 py-0.5 rounded-full ${r.aktif ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"}`}>{r.aktif ? "Aktif" : "Pasif"}</span>
                                </div>
                                <AraciPerfBar odenen={r.odenen_tutar} bekleyen={r.bekleyen_tutar} />
                            </Link>
                        )) : <p className="text-sm text-slate-500 text-center py-8">Aracı kaydı yok</p>}
                    </div>
                    <div className="hidden md:block">
                        <DetailTable columns={perfCols} rows={performans} empty="Aracı kaydı yok" mobileRender={() => null} />
                    </div>
                </>
            ) : (
                <DetailTable
                    columns={odemeCols}
                    rows={odemeler}
                    empty="Aracı ödeme kaydı yok"
                    mobileRender={(r) => (
                        <div key={r.id} className="rounded-xl border border-violet-500/15 bg-gradient-to-br from-violet-500/[0.06] to-black/20 p-3.5">
                            <div className="flex justify-between gap-2">
                                <span className="text-xs text-violet-300 font-bold">{r.araci_ad}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${r.durum === "ODENDI" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{r.durum_label}</span>
                            </div>
                            <p className="text-sm font-semibold text-white mt-2">{r.muvekkilAdSoyad}</p>
                            <p className="text-[11px] font-mono text-slate-400 mt-1">{r.tc ? `TC ${r.tc} · ` : ""}<span className="text-[#F3E5AB]">{r.dosyaNo}</span></p>
                            <div className="flex justify-between mt-2 pt-2 border-t border-white/5">
                                <span className="text-[10px] text-slate-500">{r.tarih_label}</span>
                                <span className="text-sm font-bold text-emerald-400 font-mono">{formatCurrency(r.tutar)}</span>
                            </div>
                        </div>
                    )}
                />
            )}
        </>
    );
}

function AgendaBlock({ items }) {
    if (!items?.length) {
        return (
            <p className="text-sm text-slate-500 py-4 text-center">
                Bugün planlanmış randevu yok
            </p>
        );
    }
    return (
        <ul className="space-y-2">
            {items.slice(0, 5).map((a, i) => (
                <li
                    key={i}
                    className="flex gap-3 text-xs border-b border-white/5 pb-2 last:border-0"
                >
                    <span className="font-mono text-[#D4AF37] shrink-0 w-10">
                        {a.saat}
                    </span>
                    <div className="min-w-0">
                        <p className="text-slate-200 font-medium truncate">{a.baslik}</p>
                        {a.muvekkil && (
                            <p className="text-slate-500 truncate">{a.muvekkil}</p>
                        )}
                    </div>
                </li>
            ))}
        </ul>
    );
}

export default function DashboardCorporate({
    data,
    loading,
    error,
    period,
    setPeriod,
    PERIODS,
    load,
    refreshAt,
    userDisplay,
    systemHealth,
    healthLoading,
    onRefreshHealth,
}) {
    const model = useDashboardModel(data);
    const { kpis, chartData, agenda } = model;
    const odemeAraci = data?.odeme_araci_snapshot || null;
    const [activeModule, setActiveModule] = useState("dosya");
    const [activeQueue, setActiveQueue] = useState("tahkim_gecmis");
    const [odemeSubTab, setOdemeSubTab] = useState("bekleyen");
    const [araciSubTab, setAraciSubTab] = useState("performans");

    const periodLabel =
        PERIODS.find((p) => p.id === period)?.label?.replace("g", " gün") || "30 gün";

    const hour = new Date().getHours();
    const greet =
        hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";

    const activeModDef =
        MAIN_MODULES.find((m) => m.id === activeModule) || MAIN_MODULES[0];

    const moduleCounts = useMemo(
        () => ({
            dosya: QUEUE_DEFS.reduce(
                (s, d) => s + resolveQueue(d, model).count,
                0,
            ),
            odeme: odemeAraci?.odeme_ozet?.bekleyen_sayisi ?? kpis.odeme ?? 0,
            araci:
                odemeAraci?.araci_odeme_satirlari?.filter(
                    (r) => r.durum !== "ODENDI",
                ).length ?? 0,
        }),
        [model, odemeAraci, kpis.odeme],
    );

    const activeDef =
        QUEUE_DEFS.find((q) => q.id === activeQueue) || QUEUE_DEFS[0];
    const activeData = resolveQueue(activeDef, model);
    const st = QUEUE_TONE[activeDef.tone] || QUEUE_TONE.sky;

    const queuesWithCount = useMemo(
        () =>
            QUEUE_DEFS.map((def) => ({
                def,
                ...resolveQueue(def, model),
            })).filter((q) => q.count > 0),
        [model],
    );

    const mobileNav = [
        { label: "Dosyalar", href: "/e/sigorta-dosyalari", icon: FolderOpen },
        { label: "Müvekkil", href: "/e/muvekkiller", icon: Users },
        { label: "Ödeme", href: "/e/odemeler", icon: Wallet },
        { label: "Takvim", href: "/takvim", icon: Calendar },
    ];

    return (
        <div className="min-h-full bg-[#020408] text-slate-200 pb-20 lg:pb-8">
            <header className="border-b border-white/[0.06] bg-gradient-to-b from-[#121A2F]/80 to-[#020408]">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="hidden sm:flex h-12 w-12 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#8B6914] items-center justify-center shadow-lg shrink-0">
                                <Shield className="h-6 w-6 text-slate-900" />
                            </div>
                            <div>
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB]">
                                    MBS Hukuk · Gösterge Paneli
                                </div>
                                <h1 className="text-xl sm:text-2xl font-bold text-white mt-2">
                                    {greet}, {userDisplay}
                                </h1>
                                <p className="text-xs text-slate-500 mt-1">
                                    Dosya bazlı operasyon özeti · {periodLabel} ·{" "}
                                    {refreshAt.toLocaleTimeString("tr-TR")}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="inline-flex rounded-xl border border-white/10 p-0.5 bg-black/30">
                                {PERIODS.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setPeriod(p.id)}
                                        className={`px-3 py-1.5 text-xs rounded-lg ${
                                            period === p.id
                                                ? "bg-[#D4AF37] text-slate-900 font-bold"
                                                : "text-slate-500 hover:text-white"
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                data-testid="dashboard-refresh-button"
                                onClick={load}
                                disabled={loading}
                                className="h-9 px-4 rounded-xl bg-[#D4AF37] text-slate-900 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                            >
                                <RefreshCw
                                    className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                                />
                                Yenile
                            </button>
                        </div>
                    </div>

                    {/* Kompakt özet sayılar */}
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
                        <CompactKpi
                            label="Müvekkil"
                            value={formatTR(kpis.muvekkil)}
                            href="/e/muvekkiller"
                        />
                        <CompactKpi
                            label="Dosya"
                            value={formatTR(kpis.dosya)}
                            href="/e/sigorta-dosyalari"
                            accent="sky"
                        />
                        <CompactKpi
                            label="Tahkime geçecek"
                            value={formatTR(kpis.tahkimeGidecek)}
                            href="/e/sigorta-dosyalari"
                            urgent={kpis.tahkimeGidecek > 0}
                            accent="rose"
                        />
                        <CompactKpi
                            label="Tahkimde"
                            value={formatTR(kpis.tahkim)}
                            href="/yeni/tahkim-merkezi"
                            accent="violet"
                        />
                        <CompactKpi
                            label="Bekleyen ödeme"
                            value={formatTR(kpis.odeme)}
                            href="/e/odemeler"
                            urgent={kpis.odeme > 0}
                            accent="emerald"
                        />
                        <CompactKpi
                            label="Yatar"
                            value={formatTR(kpis.yatar)}
                            href="/e/sigorta-dosyalari"
                            urgent={kpis.yatar > 0}
                            accent="gold"
                        />
                    </div>
                </div>
            </header>

            {error && (
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mt-4">
                    <div className="flex gap-2 px-4 py-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        {error}
                    </div>
                </div>
            )}

            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mt-4">
                <SystemHealthBar
                    health={systemHealth}
                    loading={healthLoading}
                    onRefresh={onRefreshHealth}
                />
            </div>

            <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
                {loading && !data ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="h-40 rounded-2xl bg-white/[0.03] animate-pulse"
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Birleşik operasyon merkezi — dosya / ödeme / aracı */}
                        <section className="rounded-2xl border border-white/[0.08] bg-[#0a1018]/80 overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.3)]">
                            <div className="px-4 sm:px-5 py-4 border-b border-white/[0.06] bg-gradient-to-r from-[#121A2F]/50 to-transparent">
                                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <Briefcase className="h-4 w-4 text-[#D4AF37]" />
                                    Operasyon Merkezi
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {activeModDef.desc}
                                </p>
                            </div>

                            {/* Ana modül sekmeleri — hemen erişim */}
                            <div className="flex gap-2 px-3 sm:px-4 py-3 overflow-x-auto scrollbar-thin border-b border-white/[0.06] bg-black/20">
                                {MAIN_MODULES.map((mod) => (
                                    <ModuleTabButton
                                        key={mod.id}
                                        mod={mod}
                                        active={activeModule === mod.id}
                                        count={moduleCounts[mod.id]}
                                        onClick={() => setActiveModule(mod.id)}
                                    />
                                ))}
                            </div>

                            {/* Dosya: alt kategori sekmeleri */}
                            {activeModule === "dosya" && (
                                <div className="flex gap-1.5 px-3 sm:px-4 py-3 overflow-x-auto scrollbar-thin border-b border-white/[0.06]">
                                    {QUEUE_DEFS.map((def) => {
                                        const q = resolveQueue(def, model);
                                        const active = def.id === activeQueue;
                                        const tone = QUEUE_TONE[def.tone] || QUEUE_TONE.sky;
                                        return (
                                            <button
                                                key={def.id}
                                                type="button"
                                                onClick={() => setActiveQueue(def.id)}
                                                className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition ${
                                                    active
                                                        ? "bg-white text-slate-900 shadow-md"
                                                        : "border border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                                                }`}
                                            >
                                                <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-slate-900" : tone.dot}`} />
                                                {def.short}
                                                <span className={`tabular-nums font-bold px-1.5 py-0.5 rounded text-[10px] ${active ? (q.count > 0 ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-600") : q.count > 0 ? tone.badge : "text-slate-600"}`}>
                                                    {q.count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="p-3 sm:p-5">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`h-2 w-2 rounded-full ${activeModDef.dot}`} />
                                            <h3 className="text-base font-semibold text-white">
                                                {activeModule === "dosya"
                                                    ? activeDef.title
                                                    : activeModDef.label}
                                            </h3>
                                            {activeModule === "dosya" && (
                                                <span className={`text-sm font-bold tabular-nums px-2 py-0.5 rounded-lg ${st.badge}`}>
                                                    {formatTR(activeData.count)} dosya
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                                            {activeModule === "dosya"
                                                ? activeDef.help
                                                : activeModDef.desc}
                                        </p>
                                    </div>
                                    <Link
                                        to={
                                            activeModule === "odeme"
                                                ? "/e/odemeler"
                                                : activeModule === "araci"
                                                  ? "/yeni/araci-merkezi"
                                                  : activeDef.id.includes("tahkim")
                                                    ? "/yeni/tahkim-merkezi"
                                                    : "/e/sigorta-dosyalari"
                                        }
                                        className="text-xs text-[#D4AF37] hover:text-[#F3E5AB] inline-flex items-center gap-1 shrink-0"
                                    >
                                        Modüle git <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>

                                {activeModule === "dosya" && (
                                    <FileTable
                                        rows={activeData.rows}
                                        empty="Bu kategoride dosya bulunmuyor."
                                    />
                                )}

                                {activeModule === "odeme" && odemeAraci && (
                                    <OdemeTakipContent
                                        snapshot={odemeAraci}
                                        periodLabel={periodLabel}
                                        subTab={odemeSubTab}
                                        onSubTab={setOdemeSubTab}
                                    />
                                )}

                                {activeModule === "araci" && odemeAraci && (
                                    <AraciPerformansContent
                                        snapshot={odemeAraci}
                                        subTab={araciSubTab}
                                        onSubTab={setAraciSubTab}
                                    />
                                )}

                                {activeModule !== "dosya" && !odemeAraci && (
                                    <p className="text-sm text-slate-500 text-center py-8">
                                        Veriler yükleniyor…
                                    </p>
                                )}
                            </div>
                        </section>

                        {/* Tüm aktif dosya kuyrukları — sadece dosya modunda */}
                        {activeModule === "dosya" && queuesWithCount.length > 0 && (
                            <section>
                                <h2 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                                    Tüm Aktif Kuyruklar ({queuesWithCount.length})
                                </h2>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {QUEUE_DEFS.map((def, idx) => (
                                        <QueuePanel
                                            key={def.id}
                                            def={def}
                                            model={model}
                                            defaultOpen={idx < 2}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Alt: grafik + ajanda */}
                        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                            <div className="xl:col-span-7 rounded-2xl border border-white/[0.08] bg-[#0a1018]/80 p-4 sm:p-5">
                                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
                                    Aylık Kayıt Trendi
                                </h3>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <BarChart data={chartData}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#1e293b"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fill: "#64748b", fontSize: 10 }}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis
                                            tick={{ fill: "#64748b", fontSize: 10 }}
                                            allowDecimals={false}
                                        />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar
                                            dataKey="dosya"
                                            name="Dosya"
                                            fill="#60A5FA"
                                            radius={[3, 3, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="muvekkil"
                                            name="Müvekkil"
                                            fill="#D4AF37"
                                            radius={[3, 3, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="xl:col-span-5 rounded-2xl border border-white/[0.08] bg-[#0a1018]/80 p-4 sm:p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                        <Calendar className="h-3.5 w-3.5 text-[#D4AF37]" />
                                        Bugünkü Ajanda
                                    </h3>
                                    <Link to="/takvim" className="text-[10px] text-[#D4AF37]">
                                        Takvim →
                                    </Link>
                                </div>
                                <AgendaBlock items={agenda} />
                            </div>
                        </div>

                        {/* Hızlı modül linkleri */}
                        <section className="flex flex-wrap gap-2">
                            {[
                                { label: "Müvekkiller", href: "/e/muvekkiller", icon: Users },
                                { label: "Sigorta Dosyaları", href: "/e/sigorta-dosyalari", icon: Briefcase },
                                { label: "Tahkim Merkezi", href: "/yeni/tahkim-merkezi", icon: Scale },
                                { label: "Ödemeler", href: "/e/odemeler", icon: Wallet },
                            ].map((m) => {
                                const Icon = m.icon;
                                return (
                                    <Link
                                        key={m.href}
                                        to={m.href}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-xs text-slate-400 hover:text-[#F3E5AB] hover:border-[#D4AF37]/30 transition"
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {m.label}
                                    </Link>
                                );
                            })}
                        </section>
                    </>
                )}
            </main>

            <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-[#020408]/95 border-t border-white/10 backdrop-blur-md">
                <div className="flex h-14 max-w-lg mx-auto">
                    {mobileNav.map((item) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                className="flex-1 flex flex-col items-center justify-center gap-0.5 text-slate-400 hover:text-[#D4AF37]"
                            >
                                <Icon className="h-5 w-5" />
                                <span className="text-[9px]">{item.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
}
